import { CONFIG } from '../config';
import { decorDef } from '../content/decor';
import { FLOORS, interiorDef, WALLS } from '../content/interior';
import { rideDef, type RideId } from '../content/rides';
import { LOTTO, lottoWeekDrawn, validLottoTicket } from '../state/rules';
import type { ShopEntry } from '../content/items';
import { HEIRLOOMS, PAYDAY_LOAN } from '../content/pawn';
import { formatMoney, round2 } from '../core/math';
import type { Random } from '../core/Random';
import type { Store } from '../state/Store';
import type { ItemId } from '../state/types';

export interface TradeResult {
  ok: boolean;
  message: string;
}

const fail = (message: string): TradeResult => ({ ok: false, message });

/** All buying and selling rules, kept free of UI so they're easy to test. */
export const Commerce = {
  buy(store: Store, entry: ShopEntry): TradeResult {
    if (!store.spend(entry.price)) return fail(`You're ${formatMoney(entry.price - store.money)} short.`);
    for (const [id, n] of Object.entries(entry.give) as [ItemId, number][]) store.give(id, n);
    return { ok: true, message: `Bought ${entry.name}.` };
  },

  buyDecor(store: Store, id: string): TradeResult {
    const def = decorDef(id);
    if (!def || !def.buyable) return fail('Not for sale.');
    if (store.hasDecor(id)) return fail('You already own that.');
    if (!store.spend(def.price)) return fail(`You're ${formatMoney(def.price - store.money)} short.`);
    store.addDecor(id);
    return { ok: true, message: `${def.name} is going up in your yard!` };
  },

  sellHeirloom(store: Store, id: string): TradeResult {
    const h = HEIRLOOMS.find((x) => x.id === id);
    if (!h || !store.state.heirlooms.includes(id)) return fail("You don't have that anymore.");
    store.state.heirlooms = store.state.heirlooms.filter((x) => x !== id);
    store.incr('pawned');
    store.earn(h.value);
    return { ok: true, message: h.quip };
  },

  takeLoan(store: Store): TradeResult {
    if (store.state.debt > 0)
      return fail('Lucky says pay off what you owe first. "I got standards. Low ones, but standards."');
    store.earn(PAYDAY_LOAN.amount);
    store.addDebt(PAYDAY_LOAN.owed);
    store.incr('loans');
    return {
      ok: true,
      message: `Lucky counts out $${PAYDAY_LOAN.amount}. You owe $${PAYDAY_LOAN.owed}, garnished on rent day. Interest is ${CONFIG.debtInterest * 100}% a week. "Don't think about the APR. I don't."`,
    };
  },

  payDebt(store: Store): TradeResult {
    const debt = store.state.debt;
    if (debt <= 0) return fail("You don't owe Lucky anything. He's almost disappointed.");
    const pay = Math.min(debt, store.money);
    if (pay <= 0) return fail('You need money to pay off debt. That is how debt works.');
    store.spend(pay);
    store.addDebt(-pay);
    return {
      ok: true,
      message:
        store.state.debt > 0
          ? `Paid ${formatMoney(pay)}. Still owe ${formatMoney(store.state.debt)}.`
          : 'Debt cleared! Lucky shakes your hand and looks genuinely sad.',
    };
  },

  sellCans(store: Store): TradeResult {
    const n = store.count('cans');
    if (n <= 0) return fail('No cans to sell. Drink more. (Responsibly.)');
    const value = round2(n * CONFIG.canValue);
    store.give('cans', -n);
    store.incr('cansRecycled', n);
    store.earn(value);
    return { ok: true, message: `Jerry weighs ${n} can${n > 1 ? 's' : ''}: ${formatMoney(value)}.` };
  },

  sellScrap(store: Store, rng: Random): TradeResult {
    const n = store.count('scrap');
    if (n <= 0) return fail('No scrap. Go find something shiny.');
    let value = 0;
    for (let i = 0; i < n; i++) value += rng.int(7, 14);
    store.give('scrap', -n);
    store.incr('scrapSold', n);
    store.earn(value);
    return {
      ok: true,
      message: `Jerry pays ${formatMoney(value)} for ${n} piece${n > 1 ? 's' : ''} of scrap. "Pleasure."`,
    };
  },

  sellCopper(store: Store, rng: Random): TradeResult {
    const lbs = store.count('copper');
    if (lbs <= 0) return fail('No copper. The condemned trailers are full of it. Allegedly.');
    const [lo, hi] = CONFIG.copperPerLb;
    let value = 0;
    for (let i = 0; i < lbs; i++) value += rng.range(lo, hi);
    value = round2(value);
    store.give('copper', -lbs);
    store.incr('copperSold', lbs);
    store.earn(value);
    return {
      ok: true,
      message: `Jerry weighs ${lbs} lb of copper and whistles: ${formatMoney(value)}. "I don't wanna know where it's from."`,
    };
  },

  buyPart(store: Store): TradeResult {
    if (store.state.camaro >= 100) return fail("The Camaro's done. Jerry respects that.");
    if (!store.spend(CONFIG.partPrice)) return fail(`Parts are $${CONFIG.partPrice}.`);
    store.give('parts', 1);
    return {
      ok: true,
      message: 'Jerry hands you a greasy part. "Fits a Camaro. Or a lawnmower. Or a boat."',
    };
  },

  sellCamaro(store: Store): TradeResult {
    if (store.state.camaro < 100) return fail('Jerry: "Not while she\'s on blocks."');
    if (store.flag('camaro_sold')) return fail('Already sold.');
    store.setFlag('camaro_sold', true);
    store.earn(CONFIG.camaroSaleValue);
    return {
      ok: true,
      message: `Jerry hands over ${formatMoney(CONFIG.camaroSaleValue)} in a paper bag. You shed a single tear. She was a good car.`,
    };
  },

  canBuyDoubleWide(store: Store): boolean {
    return store.canAfford(CONFIG.doubleWidePrice);
  },

  buyInterior(store: Store, id: string): TradeResult {
    const def = interiorDef(id);
    if (!def || !def.buyable) return fail('Not for sale.');
    if (store.hasInterior(id)) return fail('You already own that.');
    if (!store.spend(def.price)) return fail(`You're ${formatMoney(def.price - store.money)} short.`);
    store.addInterior(id);
    return { ok: true, message: `${def.name}: delivered to your living room. Lucky's nephew carried it in.` };
  },

  /** Paint or carpet: pay once, then switch back and forth for free. */
  setFinish(store: Store, which: 'walls' | 'floor', id: string): TradeResult {
    const def = (which === 'walls' ? WALLS : FLOORS).find((f) => f.id === id);
    if (!def) return fail('Not a thing.');
    const key = `finish_${which}_${id}`;
    const owned = def.price === 0 || !!store.flag(key);
    if (!owned) {
      if (!store.spend(def.price)) return fail(`You're ${formatMoney(def.price - store.money)} short.`);
      store.setFlag(key, true);
    }
    store.setFinish(which, id);
    return {
      ok: true,
      message: owned ? `Switched to ${def.name}.` : `${def.name}, installed by a guy named Rusty.`,
    };
  },

  /** Chip in to buy Lucky Acres back from Apex Capital. */
  contributeFund(store: Store, amount: number): TradeResult {
    const amt = Math.min(Math.floor(amount), Math.floor(store.money));
    if (amt <= 0) return fail('Darlene: "Sugar, that jar takes cash."');
    store.spend(amt);
    store.state.fund += amt;
    store.changed();
    return {
      ok: true,
      message: `$${amt.toLocaleString()} into the jar. Darlene writes your name on the poster.`,
    };
  },

  /** Cooter delivers it to your driveway. */
  buyRide(store: Store, id: RideId): TradeResult {
    const def = rideDef(id);
    if (def.id !== id || def.price <= 0) return fail('Cooter: "That one ain\'t for sale, bud."');
    if (store.state.rides.includes(id)) return fail('You already own that one.');
    if (!store.spend(def.price))
      return fail(
        `Cooter: "You're ${formatMoney(def.price - store.money)} short. I can't tote a note that big."`,
      );
    store.state.rides.push(id);
    store.state.ride = id;
    store.unlock('new_ride');
    store.changed();
    return {
      ok: true,
      message: `Cooter shakes your hand with grease on his. The ${def.name} is in your driveway.`,
    };
  },

  /** The Lil' Rascal: not a car, so it doesn't take up the driveway. */
  buyRascal(store: Store, price: number): TradeResult {
    if (store.has('rascal')) return fail('You already got one. One Rascal per customer.');
    if (!store.spend(price))
      return fail(
        `Cooter: "You're ${formatMoney(price - store.money)} short. She was 94 and even SHE had the cash."`,
      );
    store.give('rascal', 1);
    return {
      ok: true,
      message:
        "Cooter rolls the Lil' Rascal out his front door. It's parked right outside. Hop on (E), Space hops off.",
    };
  },

  /** Swap which of your rides is in the driveway (Cooter keeps the rest on his back lot). */
  setRide(store: Store, id: RideId): TradeResult {
    const owned = id === 'camaro' ? !store.flag('camaro_sold') : store.state.rides.includes(id);
    if (!owned) return fail("You don't own that.");
    store.state.ride = id;
    store.changed();
    return { ok: true, message: `Cooter's nephew drives the ${rideDef(id).name} over to your place.` };
  },

  /** A Mega Bucks ticket for the next drawing. */
  buyLotto(store: Store, nums: readonly number[]): TradeResult {
    if (!validLottoTicket(nums)) return fail(`Pick ${LOTTO.picks} different numbers from 1 to ${LOTTO.max}.`);
    const next = lottoWeekDrawn(store.day, store.minutes) + 1;
    const l = store.state.lotto;
    if (l.week !== next) {
      l.week = next;
      l.tickets = [];
    }
    if (l.tickets.length >= LOTTO.maxTickets)
      return fail(`${LOTTO.maxTickets} tickets a week. The clerk is cutting you off for your own good.`);
    if (!store.spend(LOTTO.price)) return fail('Two dollars. You do not have two dollars.');
    l.tickets.push([...nums].sort((a, b) => a - b));
    store.incr('lottoTickets');
    store.changed();
    return {
      ok: true,
      message: `Mega Bucks ticket: ${[...nums].sort((a, b) => a - b).join(' · ')}. Drawing Saturday at 8 PM.`,
    };
  },

  buyDoubleWide(store: Store): TradeResult {
    if (!store.spend(CONFIG.doubleWidePrice))
      return fail('Big Dave: "Come back when you got the down payment, chief."');
    store.setFlag('double_wide', true);
    store.state.status = 'won';
    store.unlock('double_wide');
    store.changed();
    return { ok: true, message: 'You signed the papers!' };
  },
};
